import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import ProfessionalService from '../../../services/ProfessionalService';
import AuthService from '../../../services/AuthService';
import ServiceHDSService from '../../../services/ServiceDHSService';

export const PDF_ICON_URL  = 'https://upload.wikimedia.org/wikipedia/commons/8/87/PDF_file_icon.svg';
export const WORD_ICON_URL = 'https://upload.wikimedia.org/wikipedia/commons/8/8d/Microsoft_Office_Word_%282019%E2%80%93present%29.svg';

export const availableLanguages = [
    "Anglais", "Français", "Espagnol",  "Chinois (Mandarin)", "Arabe"
];

export const backendToInputDate = (str) => {
    if (!str) return '';
    const parts = str.split('-');
    if (parts.length !== 3) return '';
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
};
export const inputToBackendDate = (str) => {
    if (!str) return null;
    const parts = str.split('-');
    if (parts.length !== 3) return null;
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
};
export const formatDisplayDate = (str) => {
    if (!str) return 'Non spécifié';
    const parts = str.split('-');
    if (parts.length !== 3) return str;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const splitBackendList = (str) =>
    str ? str.split(',').map(s => s.trim()).filter(Boolean) : [];

export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
export const ACCEPTED_DOC_TYPES   = [
    'image/jpeg', 'image/jpg', 'image/png', 'image/webp',
    'application/pdf',
];
export const ACCEPTED_IMAGE_ATTR = 'image/jpeg,image/jpg,image/png,image/webp';
export const ACCEPTED_DOC_ATTR   = 'image/jpeg,image/jpg,image/png,image/webp,application/pdf';

export const extractErrorMessage = (err, fallback = "Une erreur est survenue.") => {
    return err?.response?.data?.message
        || err?.response?.data?.error
        || (typeof err?.response?.data === 'string' ? err.response.data : null)
        || err?.message
        || fallback;
};

export const getFileType = (url) => {
    if (!url) return 'unknown';
    if (url === 'pdf')   return 'pdf';
    if (url === 'docx')  return 'docx';
    if (url.startsWith('blob:')) return 'image';
    const withoutQuery = url.split('?')[0];
    const ext = withoutQuery.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
    if (ext === 'pdf')  return 'pdf';
    if (ext === 'docx') return 'docx';
    return 'unknown';
};

export const getFilePreviewSentinel = (file) => {
    if (!file) return null;
    if (file.type.startsWith('image/')) return null;
    if (file.type === 'application/pdf') return 'pdf';
    return 'docx';
};

export const makeSectionStyles = (color, lightBg, borderHex, iconColor) => ({
    sectionWrapper: {
        border: `1px solid ${borderHex}33`,
        borderRadius: '18px',
        padding: '28px',
        background: `linear-gradient(160deg, ${lightBg} 0%, #ffffff 55%)`,
        position: 'relative',
        marginTop: '8px',
        boxShadow: '0 4px 18px rgba(16,24,40,0.06)',
    },
    sectionTitle:   { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' },
    requiredBadge:  {
        background: borderHex, color: '#fff', fontSize: '11px', fontWeight: 700,
        padding: '4px 13px', borderRadius: '20px', letterSpacing: '0.5px',
        textTransform: 'uppercase', boxShadow: `0 2px 8px ${borderHex}55`,
    },
    infoAlert: {
        background: '#fffaf0', border: '1px solid #ffe2a8', borderRadius: '12px',
        padding: '13px 16px', display: 'flex', alignItems: 'flex-start', gap: '10px',
        marginBottom: '22px', color: '#7a5800', fontSize: '13.5px', lineHeight: 1.55,
    },
    uploadZone: {
        border: `1.5px dashed ${borderHex}`, borderRadius: '14px', padding: '20px',
        background: '#fff', transition: 'border-color 0.2s, box-shadow 0.2s',
        boxShadow: '0 1px 3px rgba(16,24,40,0.04)',
    },
    uploadZoneValid: {
        border: '1.5px solid #34a853', borderRadius: '14px', padding: '20px',
        background: '#f3faf4', boxShadow: '0 2px 10px rgba(52,168,83,0.14)',
    },
    uploadZoneOptional: {
        border: '1.5px dashed #cbd2d9', borderRadius: '14px', padding: '20px',
        background: '#fafbfc',
    },
    missingIndicator: {
        color: borderHex, fontWeight: 600, fontSize: '12.5px',
        display: 'flex', alignItems: 'center', gap: '5px',
        background: `${borderHex}14`, padding: '3px 10px', borderRadius: '999px',
    },
    presentIndicator: {
        color: '#1e7e34', fontWeight: 600, fontSize: '12.5px',
        display: 'flex', alignItems: 'center', gap: '5px',
        background: '#34a85318', padding: '3px 10px', borderRadius: '999px',
    },
    iconColor,
    titleColor: color,
    borderHex,
});

export const identStyles    = makeSectionStyles('#1565c0', '#eef4ff', '#1976d2', '#1976d2');
export const criminalStyles = makeSectionStyles('#c62828', '#fff3f3', '#e53935', '#e53935');

export const useIdentificationPage = () => {
    const [isEditMode,                setIsEditMode]                = useState(false);
    const [isDirty,                   setIsDirty]                   = useState(false);
    const [showServiceModal,          setShowServiceModal]          = useState(false);
    const [selectedService,           setSelectedService]           = useState(null);
    const [selectedServiceId,         setSelectedServiceId]         = useState(null);
    const [selectedSubServices,       setSelectedSubServices]       = useState([]);
    const [loading,                   setLoading]                   = useState(true);
    const [saving,                    setSaving]                    = useState(false);
    const [error,                     setError]                     = useState(null);
    const [success,                   setSuccess]                   = useState(null);

    const [lightboxOpen,              setLightboxOpen]              = useState(false);
    const [lightboxSlides,            setLightboxSlides]            = useState([]);
    const [lightboxIndex,             setLightboxIndex]             = useState(0);

    const [docModal,                  setDocModal]                  = useState({ open: false, url: '', type: '', label: '' });

    const [mounted,                   setMounted]                   = useState(false);
    const [servicesData,              setServicesData]              = useState({});
    const [servicesLoading,           setServicesLoading]           = useState(true);
    const [isProfessionalAccepted,    setIsProfessionalAccepted]    = useState(false);
    const [checkingProfessionalStatus,setCheckingProfessionalStatus]= useState(false);
    const [newLanguage,               setNewLanguage]               = useState('');
    const [currentUser,               setCurrentUser]               = useState(null);
    const [userUseRef,                setUserUseRef]                = useState('');

    const [formData, setFormData] = useState({
        selectedService: null, serviceId: null, subServices: [],
        availabilityStatus: true, yearsOfExperience: '', about: '', languages: [],
        contact1Name: '', contact1Phone: '', contact1Relationship: '',
        contact2Name: '', contact2Phone: '', contact2Relationship: '',
        cv: null, cvPreview: null, cvUrl: '', cvRawUrl: '',
        identificationSubmitted: false,
        documentType: 'CNI', status: 'PENDING', rejectionReason: '',
        documentName: '', documentIssueDate: '', documentExpiryDate: '',
        issuingAuthority: '', country: '',
        profilePhoto:            null, profilePhotoPreview:            null, profilePhotoUrl:            '', profilePhotoRawUrl:            '',
        identificationFrontDoc:  null, identificationFrontDocPreview:  null, identificationFrontDocUrl:  '', identificationFrontDocRawUrl:  '',
        identificationBackDoc:   null, identificationBackDocPreview:   null, identificationBackDocUrl:   '', identificationBackDocRawUrl:   '',
        locationPlan:            null, locationPlanPreview:            null, locationPlanUrl:            '', locationPlanRawUrl:            '',
        criminalRecordName: 'Casier judiciaire', criminalRecordIssueDate: '', criminalRecordExpiryDate: '',
        criminalRecordIssuingAuthority: '', criminalRecordCountry: '',
        criminalRecordFrontDoc:  null, criminalRecordFrontDocPreview:  null, criminalRecordFrontDocUrl:  '', criminalRecordFrontDocRawUrl:  '',
    });

    const [dateErrors, setDateErrors] = useState({
        documentIssueDate: '', documentExpiryDate: '',
        criminalRecordIssueDate: '', criminalRecordExpiryDate: '',
    });

    useEffect(() => { setMounted(true); return () => setMounted(false); }, []);

    useEffect(() => {
        const checkStatus = async () => {
            try {
                setCheckingProfessionalStatus(true);
                const user = AuthService.getCurrentUser();
                if (user?.useRef) {
                    const accepted = await ProfessionalService.isProfessionalAccepted(user.useRef);
                    setIsProfessionalAccepted(accepted);
                }
            } catch (e) {
                console.error('Erreur vérification statut professionnel:', e);
            } finally { setCheckingProfessionalStatus(false); }
        };
        if (AuthService.isAuthenticated()) checkStatus();
    }, []);

    useEffect(() => {
        const fetchServices = async () => {
            try {
                setServicesLoading(true);
                const services = await ServiceHDSService.getAllServices();
                const transformed = {};
                services.forEach(s => {
                    transformed[s.name] = {
                        id:          s.id,
                        name:        s.name,
                        icon:        s.mediaUrlPresigned || s.mediaUrl || 'assets/images/icon/default.png',
                        subServices: s.subServices.map(sub => sub.name),
                    };
                });
                setServicesData(transformed);
            } catch (e) {
                console.error('Erreur récupération services:', e);
                setError('Impossible de charger les services');
            } finally { setServicesLoading(false); }
        };
        fetchServices();
    }, []);

    useEffect(() => {
        const fetchProfessionalData = async () => {
            try {
                setLoading(true); setError(null);
                const user = AuthService.getCurrentUser();
                setCurrentUser(user);
                if (!user?.useRef) throw new Error('Utilisateur non connecté');
                setUserUseRef(user.useRef);
                const p = await ProfessionalService.getProfessionalByUseRef(user.useRef);
                mapProfessionalToForm(p);
                setIsEditMode(true);
                setIsDirty(false);
            } catch (e) {
                console.error('Erreur récupération données professionnelles:', e);
                if (!e.response || e.response.status !== 404) {
                    setError('Impossible de charger les données professionnelles');
                } else {
                    setIsEditMode(true);
                }
            } finally { setLoading(false); }
        };
        fetchProfessionalData();
    }, []);

    const mapProfessionalToForm = (p) => {
        const identification = p.identification || {};
        const criminalRecord = p.criminalRecord || {};

        const docTypeMap = {
            'CNI': "Carte Nationale d'Identité",
            'PASSPORT': 'Passeport',
            'DRIVING_LICENSE': 'Permis de conduire',
            'RESIDENCE_PERMIT': 'Titre de séjour'
        };
        const identDocName = identification.documentName || docTypeMap[identification.documentType] || '';

        setFormData(prev => ({
            selectedService:    p.selectedService || null,
            serviceId:          p.serviceId       || null,
            subServices:        splitBackendList(p.subServices),
            availabilityStatus: p.availabilityStatus !== undefined ? p.availabilityStatus : true,
            yearsOfExperience:  p.yearsOfExperience || '',
            about:              p.about || '',
            languages:          splitBackendList(p.languages),

            contact1Name: p.contact1Name || '',
            contact1Phone: p.contact1Phone || '',
            contact1Relationship: p.contact1Relationship || '',
            contact2Name: p.contact2Name || '',
            contact2Phone: p.contact2Phone || '',
            contact2Relationship: p.contact2Relationship || '',

            cv: null, cvPreview: null,
            cvUrl: p.cvUrlPresigned || p.cvUrl || prev.cvUrl || '',
            cvRawUrl: p.cvUrl || prev.cvRawUrl || '',

            identificationSubmitted: p.identificationSubmitted || false,
            documentType:       identification.documentType || 'CNI',
            documentName:       identDocName,
            status:             p.status || 'PENDING',
            rejectionReason:    p.rejectionReason || '',
            documentIssueDate:  identification.issueDate  ? backendToInputDate(identification.issueDate)  : '',
            documentExpiryDate: identification.expiryDate ? backendToInputDate(identification.expiryDate) : '',
            issuingAuthority:   identification.issuingAuthority || '',
            country:            identification.country || '',

            profilePhoto:           null, profilePhotoPreview:           null,
            profilePhotoUrl:        p.profilePhotoUrlPresigned || p.profilePhotoUrl || prev.profilePhotoUrl || '',
            profilePhotoRawUrl:     p.profilePhotoUrl || prev.profilePhotoRawUrl || '',

            identificationFrontDoc: null, identificationFrontDocPreview: null,
            identificationFrontDocUrl:    identification.documentFrontUrlPresigned || identification.documentFrontUrl || prev.identificationFrontDocUrl || '',
            identificationFrontDocRawUrl: identification.documentFrontUrl || prev.identificationFrontDocRawUrl || '',

            identificationBackDoc:  null, identificationBackDocPreview:  null,
            identificationBackDocUrl:     identification.documentBackUrlPresigned  || identification.documentBackUrl  || prev.identificationBackDocUrl  || '',
            identificationBackDocRawUrl:  identification.documentBackUrl || prev.identificationBackDocRawUrl || '',

            locationPlan:           null, locationPlanPreview:           null,
            locationPlanUrl:        p.locationPlanUrlPresigned || p.locationPlanUrl || prev.locationPlanUrl || '',
            locationPlanRawUrl:     p.locationPlanUrl || prev.locationPlanRawUrl || '',

            criminalRecordName:           criminalRecord.documentName || 'Casier judiciaire',
            criminalRecordIssueDate:  criminalRecord.issueDate  ? backendToInputDate(criminalRecord.issueDate)  : '',
            criminalRecordExpiryDate: criminalRecord.expiryDate ? backendToInputDate(criminalRecord.expiryDate) : '',
            criminalRecordIssuingAuthority: criminalRecord.issuingAuthority || '',
            criminalRecordCountry:          criminalRecord.country || '',

            criminalRecordFrontDoc:   null, criminalRecordFrontDocPreview: null,
            criminalRecordFrontDocUrl:    criminalRecord.documentFrontUrlPresigned || criminalRecord.documentFrontUrl || prev.criminalRecordFrontDocUrl || '',
            criminalRecordFrontDocRawUrl: criminalRecord.documentFrontUrl || prev.criminalRecordFrontDocRawUrl || '',
        }));
        setIsDirty(false);
    };

    const validateDates = () => {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const errors = { documentIssueDate: '', documentExpiryDate: '', criminalRecordIssueDate: '', criminalRecordExpiryDate: '' };
        let valid = true;

        const checkPair = (issueField, expiryField) => {
            if (formData[issueField]) {
                const d = new Date(formData[issueField]);
                if (d > today) { errors[issueField] = "La date de délivrance ne peut pas être dans le futur."; valid = false; }
                if (formData[expiryField]) {
                    const e = new Date(formData[expiryField]);
                    if (e <= d) { errors[expiryField] = "La date d'expiration doit être postérieure à la date de délivrance."; valid = false; }
                }
            }
            if (formData[expiryField] && !formData[issueField]) {
                errors[issueField] = "Veuillez renseigner la date de délivrance."; valid = false;
            }
        };

        checkPair('documentIssueDate', 'documentExpiryDate');
        checkPair('criminalRecordIssueDate', 'criminalRecordExpiryDate');
        setDateErrors(errors);
        return valid;
    };

    const handleDateChange = (fieldName, value) => {
        setFormData(prev => ({ ...prev, [fieldName]: value }));
        setDateErrors(prev => ({ ...prev, [fieldName]: '' }));
        setIsDirty(true);
    };

    const handleOpenPreview = (url, type, label = '') => {
        if (!url || url === 'pdf' || url === 'docx') return;
        if (type === 'image') {
            setLightboxSlides([{ src: url }]);
            setLightboxIndex(0);
            setLightboxOpen(true);
            return;
        }
        setDocModal({ open: true, url, type, label });
    };

    const handleCloseDocModal = () => setDocModal({ open: false, url: '', type: '', label: '' });
    const handleCloseLightbox = () => setLightboxOpen(false);

    const handleFileUpload = (event, fieldName) => {
        const file = event.target.files[0];
        if (!file) return;

        if (fieldName === 'profilePhoto') {
            if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
                toast.error('Photo de profil : seules les images JPEG, PNG, WEBP sont autorisées.');
                return;
            }
        } else {
            if (!ACCEPTED_DOC_TYPES.includes(file.type)) {
                toast.error('Document : seuls les formats image (JPEG, PNG, WEBP) et PDF sont autorisés.');
                return;
            }
        }

        if (file.size > 10 * 1024 * 1024) { toast.error('La taille maximale est de 10 MB.'); return; }

        const previewField = `${fieldName}Preview`;
        if (file.type.startsWith('image/')) {
            const blobUrl = URL.createObjectURL(file);
            setFormData(prev => ({ ...prev, [fieldName]: file, [previewField]: blobUrl }));
        } else if (file.type === 'application/pdf') {
            setFormData(prev => ({ ...prev, [fieldName]: file, [previewField]: 'pdf' }));
        } else {
            setFormData(prev => ({ ...prev, [fieldName]: file, [previewField]: 'docx' }));
        }
        event.target.value = '';
        setIsDirty(true);
    };

    const handleRemoveFile = (fieldName) => {
        const previewField = `${fieldName}Preview`;
        const urlField      = `${fieldName}Url`;
        const rawUrlField   = `${fieldName}RawUrl`;
        const preview = formData[previewField];
        if (preview && preview !== 'pdf' && preview !== 'docx' && preview?.startsWith('blob:')) {
            URL.revokeObjectURL(preview);
        }
        setFormData(prev => ({ ...prev, [fieldName]: null, [previewField]: null, [urlField]: '', [rawUrlField]: '' }));
        setIsDirty(true);
    };

    const validateForm = () => {
        const hasCv = formData.cv || formData.cvUrl;
        const hasProfilePhoto = formData.profilePhoto || formData.profilePhotoUrl;
        const hasIdentFront = formData.identificationFrontDoc || formData.identificationFrontDocUrl;
        const hasLocationPlan = formData.locationPlan || formData.locationPlanUrl;

        const requiredFields = [
            { field: formData.selectedService,        name: 'Service' },
            { field: formData.serviceId,              name: 'ID du service' },
            { field: formData.subServices.length > 0, name: 'Sous-services' },
            { field: formData.yearsOfExperience !== '' && formData.yearsOfExperience !== null, name: "Années d'expérience" },
            { field: formData.about,                  name: 'À propos de vous' },
            { field: formData.languages.length > 0,   name: 'Langues parlées' },
            { field: hasCv,                           name: 'CV' },
            { field: hasProfilePhoto,                 name: 'Photo de profil' },
            { field: hasLocationPlan,                 name: 'Plan de localisation' },
            { field: formData.contact1Name,           name: 'Nom du contact 1' },
            { field: formData.contact1Phone,          name: 'Téléphone du contact 1' },
            { field: formData.contact2Name,           name: 'Nom du contact 2' },
            { field: formData.contact2Phone,          name: 'Téléphone du contact 2' },
            { field: hasIdentFront, name: "Document d'identification (recto)" },
            { field: formData.documentType, name: 'Type de document' },
            { field: formData.country, name: "Pays d'émission (Identité)" },
            { field: formData.issuingAuthority, name: "Autorité émettrice (Identité)" },
            { field: formData.documentIssueDate, name: "Date de délivrance (Identité)" },
        ];

        const missing = requiredFields.filter(f => !f.field);
        if (missing.length > 0) {
            toast.error(`Champs obligatoires manquants : ${missing.map(f => f.name).join(', ')}`);
            return false;
        }

        // Validation de la complétude du Casier Judiciaire s'il a été entamé
        const isCriminalRecordPartiallyFilled = !!formData.criminalRecordIssueDate ||
            !!formData.criminalRecordExpiryDate ||
            !!formData.criminalRecordIssuingAuthority ||
            !!formData.criminalRecordCountry ||
            !!formData.criminalRecordFrontDoc ||
            !!formData.criminalRecordFrontDocUrl;

        if (isCriminalRecordPartiallyFilled) {
            const crFields = [
                { field: formData.criminalRecordIssueDate, name: "Date de délivrance (Casier)" },
                { field: formData.criminalRecordExpiryDate, name: "Date d'expiration (Casier)" },
                { field: formData.criminalRecordIssuingAuthority, name: "Autorité émettrice (Casier)" },
                { field: formData.criminalRecordCountry, name: "Pays d'émission (Casier)" },
                { field: formData.criminalRecordFrontDoc || formData.criminalRecordFrontDocUrl, name: "Document (Casier)" },
            ];
            const missingCr = crFields.filter(f => !f.field);
            if (missingCr.length > 0) {
                toast.error(`Veuillez compléter tous les champs du Casier Judiciaire ou réinitialisez le bloc : ${missingCr.map(f => f.name).join(', ')}`);
                return false;
            }
        }

        if (!validateDates()) { toast.error('Veuillez corriger les erreurs de dates.'); return false; }
        return true;
    };

    const handleResetCriminalRecord = () => {
        setFormData(prev => ({
            ...prev,
            criminalRecordIssueDate: '',
            criminalRecordExpiryDate: '',
            criminalRecordIssuingAuthority: '',
            criminalRecordCountry: '',
            criminalRecordFrontDoc: null,
            criminalRecordFrontDocPreview: null,
            criminalRecordFrontDocUrl: '',
            criminalRecordFrontDocRawUrl: '',
        }));
        setDateErrors(prev => ({ ...prev, criminalRecordIssueDate: '', criminalRecordExpiryDate: '' }));
        setIsDirty(true);
        toast.info("Bloc Casier Judiciaire réinitialisé.");
    };

    const handleOpenServiceModal   = () => setShowServiceModal(true);
    const handleCloseServiceModal  = () => { setShowServiceModal(false); setSelectedService(null); setSelectedServiceId(null); setSelectedSubServices([]); };
    const handleSelectService      = (key, id) => { setSelectedService(key); setSelectedServiceId(id); setSelectedSubServices([]); };
    const handleSubServiceChange   = (sub) => setSelectedSubServices(prev => prev.includes(sub) ? prev.filter(i => i !== sub) : [...prev, sub]);
    const handleSaveService        = () => {
        if (selectedService && selectedSubServices.length > 0) {
            setFormData(prev => ({ ...prev, selectedService, serviceId: selectedServiceId, subServices: selectedSubServices }));
            handleCloseServiceModal();
            setIsDirty(true);
        }
    };
    const handleRemoveService = () => {
        setFormData(prev => ({ ...prev, selectedService: null, serviceId: null, subServices: [] }));
        setIsDirty(true);
    };

    const handleLanguageChange = (e) => {
        const lang = e.target.value;
        if (lang && !formData.languages.includes(lang)) {
            setFormData(prev => ({ ...prev, languages: [...prev.languages, lang] }));
            setNewLanguage('');
            setIsDirty(true);
        }
    };
    const handleRemoveLanguage = (lang) => {
        setFormData(prev => ({ ...prev, languages: prev.languages.filter(l => l !== lang) }));
        setIsDirty(true);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        let newFields = { [name]: type === 'checkbox' ? checked : value };

        if (name === 'documentType') {
            const docMap = {
                'CNI': "Carte Nationale d'Identité",
                'PASSPORT': 'Passeport',
                'DRIVING_LICENSE': 'Permis de conduire',
                'RESIDENCE_PERMIT': 'Titre de séjour'
            };
            newFields.documentName = docMap[value] || '';
        }

        setFormData(prev => ({ ...prev, ...newFields }));
        setIsDirty(true);
    };

    const handleSave = async () => {
        try {
            setSaving(true); setError(null); setSuccess(null);
            if (!validateForm()) { setSaving(false); return; }
            if (!userUseRef) throw new Error('UseRef non disponible');

            // ✅ Vérification stricte: Le Casier Judiciaire est-il entièrement et validement rempli ?
            const isCriminalRecordFilled = !!formData.criminalRecordIssueDate &&
                !!formData.criminalRecordExpiryDate &&
                !!formData.criminalRecordIssuingAuthority &&
                !!formData.criminalRecordCountry &&
                (!!formData.criminalRecordFrontDoc || !!formData.criminalRecordFrontDocUrl);

            // Si rempli -> PENDING + true. Sinon -> null + false.
            const status = isCriminalRecordFilled ? 'PENDING' : null;
            const identificationSubmitted = isCriminalRecordFilled;

            const professionalRequestDto = {
                selectedService:    formData.selectedService,
                serviceId:          formData.serviceId,
                subServices:        formData.subServices.join(', '),
                languages:          formData.languages.join(', '),
                availabilityStatus: formData.availabilityStatus,
                yearsOfExperience:  formData.yearsOfExperience !== '' ? parseInt(formData.yearsOfExperience) : null,
                about:              formData.about,
                status:             status,
                identificationSubmitted: identificationSubmitted,

                contact1Name: formData.contact1Name,
                contact1Phone: formData.contact1Phone,
                contact1Relationship: formData.contact1Relationship,
                contact2Name: formData.contact2Name,
                contact2Phone: formData.contact2Phone,
                contact2Relationship: formData.contact2Relationship,

                identification: {
                    documentType:     formData.documentType,
                    documentName:     formData.documentName,
                    issueDate:        inputToBackendDate(formData.documentIssueDate),
                    expiryDate:       inputToBackendDate(formData.documentExpiryDate),
                    issuingAuthority: formData.issuingAuthority,
                    country:          formData.country,
                    documentFrontUrl: formData.identificationFrontDoc ? null : (formData.identificationFrontDocRawUrl || null),
                    documentBackUrl:  formData.identificationBackDoc  ? null : (formData.identificationBackDocRawUrl  || null),
                },
                criminalRecord: isCriminalRecordFilled ? {
                    documentName:     'Casier judiciaire',
                    issueDate:        inputToBackendDate(formData.criminalRecordIssueDate),
                    expiryDate:       inputToBackendDate(formData.criminalRecordExpiryDate),
                    issuingAuthority: formData.criminalRecordIssuingAuthority,
                    country:          formData.criminalRecordCountry,
                    documentFrontUrl: formData.criminalRecordFrontDoc ? null : (formData.criminalRecordFrontDocRawUrl || null),
                } : null,
                lastModifiedBy: currentUser?.username || 'user',
            };

            const filesToUpload = {
                profilePhoto:           formData.profilePhoto           || undefined,
                identificationFrontDoc: formData.identificationFrontDoc || undefined,
                identificationBackDoc:  formData.identificationBackDoc  || undefined,
                criminalRecordFrontDoc: formData.criminalRecordFrontDoc || undefined,
                locationPlan:           formData.locationPlan           || undefined,
                cv:                     formData.cv                     || undefined,
            };

            await ProfessionalService.updateProfessionalByUseRefWithFiles(userUseRef, professionalRequestDto, filesToUpload);

            setSuccess('Informations professionnelles sauvegardées avec succès');
            toast.success('Informations sauvegardées !', { position: 'top-right', autoClose: 5000 });
            setIsDirty(false);

            const updated = await ProfessionalService.getProfessionalByUseRef(userUseRef);
            mapProfessionalToForm(updated);
        } catch (e) {
            console.error('Erreur sauvegarde:', e);
            const backendMessage = extractErrorMessage(e, 'Erreur lors de la sauvegarde.');
            setError(backendMessage);
            toast.error(backendMessage);
        } finally { setSaving(false); }
    };

    const handleCancel = () => {
        setDateErrors({ documentIssueDate: '', documentExpiryDate: '', criminalRecordIssueDate: '', criminalRecordExpiryDate: '' });
        setNewLanguage('');
        setIsDirty(false);
        const reload = async () => {
            try {
                if (userUseRef) {
                    const p = await ProfessionalService.getProfessionalByUseRef(userUseRef);
                    mapProfessionalToForm(p);
                }
            } catch (e) {
                console.error('Erreur rechargement:', e);
            }
        };
        reload();
    };

    const shouldDisableIdentification = isProfessionalAccepted;

    return {
        isEditMode, setIsEditMode,
        isDirty, setIsDirty,
        showServiceModal, setShowServiceModal,
        selectedService, setSelectedService,
        selectedServiceId, setSelectedServiceId,
        selectedSubServices, setSelectedSubServices,
        loading, saving, error, success,
        lightboxOpen, lightboxSlides, lightboxIndex, setLightboxIndex, handleCloseLightbox,
        docModal, handleCloseDocModal,
        mounted,
        servicesData, servicesLoading,
        isProfessionalAccepted, checkingProfessionalStatus,
        newLanguage, setNewLanguage,
        currentUser, userUseRef,
        formData, setFormData,
        dateErrors,
        shouldDisableIdentification,
        handleDateChange,
        handleOpenPreview,
        handleFileUpload, handleRemoveFile,
        validateForm, validateDates,
        handleOpenServiceModal, handleCloseServiceModal, handleSelectService,
        handleSubServiceChange, handleSaveService, handleRemoveService,
        handleLanguageChange, handleRemoveLanguage,
        handleSave, handleCancel, handleInputChange, handleResetCriminalRecord,
    };
};

export default useIdentificationPage;